import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Button, Tile, InlineNotification, SkeletonPlaceholder, Search, SkeletonText, Tag, TimePicker, TimePickerSelect, SelectItem, Modal, Dropdown } from '@carbon/react';
import { ChevronLeft, ChevronRight, Add, Close, Checkmark, TrashCan, Filter } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import PageContainer from '../components/PageContainer';
import { useTheme } from '../contexts/ThemeContext';
import { usePermissions } from '../contexts/PermissionContext';
import './Menu.css';

// Mapiranje vrednosti tipa obroka sa backenda na numeričku vrednost koja se šalje u POST
const MEAL_TYPE_VALUE_MAP = {
  Breakfast: 0,
  Lunch: 1,
  Dinner: 2,
  Snack: 3,
  LateDinner: 4,
};

// Podrazumevani tip obroka na osnovu vremena početka (sati)
// 8-11 doručak, 11-13 užina, 13-16 ručak, 16-18 užina, 18-21 večera, 21-24 kasna večera
const getDefaultMealTypeValue = (hour) => {
  const h = parseInt(hour, 10);
  if (Number.isNaN(h)) return null;
  if (h >= 8 && h < 11) return 0;   // Doručak
  if (h >= 11 && h < 13) return 3;  // Užina
  if (h >= 13 && h < 16) return 1;  // Ručak
  if (h >= 16 && h < 18) return 3;  // Užina
  if (h >= 18 && h < 21) return 2;  // Večera
  if (h >= 21 && h < 24) return 4;  // Kasna večera
  return null;
};

const Menu = () => {
  // Jelovnik (pregled) je vidljiv svim zaposlenima, ali dodavanje i brisanje
  // obroka u jelovniku zahteva dozvolu `menus.manage` („Pravljenje jelovnika").
  // Backend je traži za POST/PUT/DELETE /api/DailyMenu i DELETE /api/DailyMenu/week.
  const { can } = usePermissions();
  const canManageMenus = can('menus.manage');

  const [weekOffset, setWeekOffset] = useState(0);
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dietPlanId, setDietPlanId] = useState('');
  const [allMenuNames, setAllMenuNames] = useState([]);
  const [menuNamesLoading, setMenuNamesLoading] = useState(false);
  const [dietTypes, setDietTypes] = useState([]);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [meals, setMeals] = useState([]);
  const [mealsLoading, setMealsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
  const [dayViewDate, setDayViewDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageFading, setPageFading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [startTime, setStartTime] = useState({ hour: '09', minute: '00' });
  const [endTime, setEndTime] = useState({ hour: '10', minute: '00' });
  const [repeatEveryWeek, setRepeatEveryWeek] = useState(0);
  const [mealTypes, setMealTypes] = useState([]);
  const [selectedMealType, setSelectedMealType] = useState(() => getDefaultMealTypeValue('09'));
  const [menuName, setMenuName] = useState('');
  const [notification, setNotification] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState(null);
  const [isDeleteWeekModalOpen, setIsDeleteWeekModalOpen] = useState(false);
  const [stepTransition, setStepTransition] = useState('entering');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const stepTransitionTimeout = useRef(null);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [viewingMenu, setViewingMenu] = useState(null);
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);
  const [isMealImageModalOpen, setIsMealImageModalOpen] = useState(false);
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isFilterExiting, setIsFilterExiting] = useState(false);
  // Tip ishrane iz URL-a (npr. dolazak sa dashboarda: /jelovnik?dietPlan=Gluten-free)
  const [searchParams] = useSearchParams();
  const dietPlanFromUrl = searchParams.get('dietPlan');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchMenuData();
  }, [weekOffset, dietPlanId]);

  useEffect(() => {
    fetchAllMenuNames();
    fetchDietTypes();
    fetchMealTypes();
  }, []);

  useEffect(() => {
    if (dietTypes.length === 0 || dietPlanId) return;
    // Ako je tip ishrane prosleđen kroz URL, izaberi njega; inače prvi dostupni
    const fromUrl = dietPlanFromUrl
      ? dietTypes.find((dt) => dt.name === dietPlanFromUrl)
      : null;
    const initial = fromUrl || dietTypes[0];
    setMenuName(initial.name);
    setDietPlanId(initial.id);
  }, [dietTypes, dietPlanFromUrl]);

  // Podrazumevani tip obroka se menja u zavisnosti od izabranog vremena početka
  useEffect(() => {
    const defaultValue = getDefaultMealTypeValue(startTime.hour);
    if (defaultValue !== null) {
      setSelectedMealType(defaultValue);
    }
  }, [startTime.hour]);

  const fetchDietTypes = async () => {
    try {
      const response = await axiosInstance.get('/api/DietPlan');
      const data = response.data?.data || [];
      setDietTypes(data);
    } catch (err) {
      console.error('Greška pri učitavanju tipova dijete:', err);
    }
  };

  const fetchAllMenuNames = async () => {
    setMenuNamesLoading(true);
    try {
      const response = await axiosInstance.get('/api/DailyMenu/all-menu-names');
      if (response.data.success) {
        setAllMenuNames(response.data.data || []);
      }
    } catch (err) {
      console.error('Greška pri učitavanju naziva jelovnika:', err);
      setAllMenuNames([]);
    } finally {
      setMenuNamesLoading(false);
    }
  };

  const fetchMealTypes = async () => {
    try {
      const response = await axiosInstance.get('/api/DailyMenu/meal-types');
      const data = response.data?.mealTypes || response.data?.data?.mealTypes || [];
      setMealTypes(data);
    } catch (err) {
      console.error('Greška pri učitavanju tipova obroka:', err);
      setMealTypes([]);
    }
  };

  const getMealTypeDisplayName = (value) => {
    const found = mealTypes.find((type) => MEAL_TYPE_VALUE_MAP[type.value] === value);
    return found?.displayName || '-';
  };

  const fetchMenuData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!dietPlanId) {
        setLoading(false);
        return;
      }
      const url = `/api/DailyMenu?dietPlanId=${dietPlanId}&weekOffset=${weekOffset}`;
      const response = await axiosInstance.get(url);
      if (response.data.success) {
        setMenuData(response.data.data);
      }
    } catch (err) {
      setError('Greška pri učitavanju jelovnika');
      console.error('Error fetching menu:', err);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    setWeekOffset(weekOffset + direction);
  };

  const navigateDay = (direction) => {
    if (dayViewDate) {
      const newDate = new Date(dayViewDate);
      newDate.setDate(newDate.getDate() + direction);
      setDayViewDate(newDate);
    }
  };

  const goToCurrentWeek = () => {
    setWeekOffset(0);
  };

  const goToToday = () => {
    setDayViewDate(new Date());
  };

  const handleCloseMobileFilter = () => {
    setIsFilterExiting(true);
    setTimeout(() => {
      setIsMobileFilterOpen(false);
      setIsFilterExiting(false);
    }, 300);
  };

  const openSidePanel = (date) => {
    // Dodavanje obroka u jelovnik zahteva dozvolu `menus.manage`.
    if (!canManageMenus) return;
    setSelectedDate(date);
    setIsSidePanelOpen(true);
    fetchMeals();
  };

  const fetchMeals = async () => {
    setMealsLoading(true);
    try {
      // Izbornik prikazuje sve obroke ustanove; server ih kešira po ustanovi
      // (GET /api/Meal je paginiran i nije pogodan za birač).
      const response = await axiosInstance.get('/api/Meal/lookup');
      if (response.data.success) {
        setMeals(response.data.data || []);
      }
    } catch (err) {
      console.error('Greška pri učitavanju obroka:', err);
      setMeals([]);
    } finally {
      setMealsLoading(false);
    }
  };

  const closeSidePanel = () => {
    setIsSidePanelOpen(false);
    if (selectedDate) {
      setSelectedDates(selectedDates.filter(d => formatDateKey(d) !== formatDateKey(selectedDate)));
    }
    setSelectedDate(null);
    setSearchTerm('');
    setPage(1);
    setCurrentStep(0);
    setSelectedMeal(null);
    setStartTime({ hour: '09', minute: '00' });
    setEndTime({ hour: '10', minute: '00' });
    setSelectedMealType(getDefaultMealTypeValue('09'));
    setMenuName(dietTypes.find(dt => dt.id === dietPlanId)?.name || '');
  };

  const handleMealSelect = (meal) => {
    // Toggle selection - deselect if already selected
    if (selectedMeal && selectedMeal.id === meal.id) {
      // Onemogući deselect ako je prelazak u toku
      if (isTransitioning) {
        return;
      }
      
      setSelectedMeal(null);
      // Otkaži prelazak na sledeći korak
      if (stepTransitionTimeout.current) {
        clearTimeout(stepTransitionTimeout.current);
        stepTransitionTimeout.current = null;
      }
      return;
    }
    
    setSelectedMeal(meal);
    // Nakon 1 sekunde pređi na sledeći korak
    stepTransitionTimeout.current = setTimeout(() => {
      setIsTransitioning(true);
      setStepTransition('exiting');
      setTimeout(() => {
        setCurrentStep(1);
        setStepTransition('entering');
        setIsTransitioning(false);
      }, 150);
    }, 1000);
  };

  const goToStep = (step) => {
    setStepTransition('exiting');
    setTimeout(() => {
      setCurrentStep(step);
      setStepTransition('entering');
    }, 150);
  };

  const handleStepClick = (targetStep) => {
    // Dozvoli vraćanje unazad
    if (targetStep < currentStep) {
      goToStep(targetStep);
      return;
    }

    // Ako pokušava da ide na trenutni korak, ne radi ništa
    if (targetStep === currentStep) {
      return;
    }

    // Proveri da li može da ide napred
    if (targetStep > currentStep) {
      // Za korak 1: mora da ima izabran obrok
      if (targetStep === 1 && !selectedMeal) {
        setNotification({
          kind: 'warning',
          title: 'Obavezno polje',
          subtitle: 'Prvo morate izabrati obrok pre nego što nastavite.'
        });
        setTimeout(() => setNotification(null), 3000);
        return;
      }

      // Za korak 2: mora da ima izabran obrok (korak 1 već završen)
      if (targetStep === 2 && currentStep < 1) {
        setNotification({
          kind: 'warning',
          title: 'Obavezno polje',
          subtitle: 'Prvo morate završiti prethodne korake.'
        });
        setTimeout(() => setNotification(null), 3000);
        return;
      }

      // Ako pokušava da preskoči korake
      if (targetStep > currentStep + 1) {
        setNotification({
          kind: 'warning',
          title: 'Upozorenje',
          subtitle: 'Morate završiti trenutni korak pre nego što nastavite dalje.'
        });
        setTimeout(() => setNotification(null), 3000);
        return;
      }
    }
  };

  const nextPage = () => {
    const totalPages = Math.ceil(filteredMeals.length / pageSize);
    if (page < totalPages) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev + 1);
        setPageFading(false);
      }, 300);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev - 1);
        setPageFading(false);
      }, 300);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const days = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
    return {
      dayName: days[date.getDay()],
      date: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear()
    };
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('sr-Latn-RS', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const truncateText = (text, maxLength = 15) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const toggleDateSelection = (date) => {
    const dateKey = formatDateKey(date);
    const isSelected = selectedDates.some(d => formatDateKey(d) === dateKey);
    
    if (isSelected) {
      setSelectedDates(selectedDates.filter(d => formatDateKey(d) !== dateKey));
    } else {
      setSelectedDates([...selectedDates, date]);
      openSidePanel(date);
    }
  };

  const groupMenusByDay = () => {
    if (!menuData || !menuData.menus) return {};
    
    const grouped = {};
    menuData.menus.forEach(menu => {
      const dayKey = new Date(menu.startTime).toDateString();
      if (!grouped[dayKey]) {
        grouped[dayKey] = [];
      }
      grouped[dayKey].push(menu);
    });
    
    return grouped;
  };

  const getSuggestedTimes = () => {
    if (!selectedDate || !menuData) return [];
    
    // Grupiši menije po danu
    const groupedMenus = groupMenusByDay();
    const selectedDateKey = selectedDate.toDateString();
    
    // Nađi sve obroke za izabrani datum
    const menusForDate = groupedMenus[selectedDateKey] || [];

    let baseTime;
    
    // Ako postoje obroci, počni 3 sata nakon poslednjeg
    if (menusForDate.length > 0) {
      const sorted = [...menusForDate].sort((a, b) => 
        new Date(a.endTime) - new Date(b.endTime)
      );
      const lastMeal = sorted[sorted.length - 1];
      const lastEndTime = new Date(lastMeal.endTime);
      baseTime = new Date(lastEndTime.getTime() + 3 * 60 * 60 * 1000);
    } else {
      // Ako nema obroka, počni od 7:00
      baseTime = new Date(selectedDate);
      baseTime.setHours(7, 0, 0, 0);
    }

    // Ograničenja: minimum 7:00, maksimum završetak do 22:00
    const minHour = 7;
    const maxEndHour = 22;

    // Generiši slotove sa različitim trajanjima
    const suggestions = [];
    const durations = [1, 1, 1.5, 1, 2, 1.5, 1, 1.5, 2]; // sati trajanja
    let currentTime = new Date(baseTime);
    
    // Osiguraj da početno vreme nije pre 7:00
    if (currentTime.getHours() < minHour) {
      currentTime.setHours(minHour, 0, 0, 0);
    }
    
    for (let i = 0; i < 9; i++) {
      const startTime = new Date(currentTime.getTime() + i * 60 * 60 * 1000); // svaki sat kasnije
      const endTime = new Date(startTime.getTime() + durations[i] * 60 * 60 * 1000);
      
      // Proveri da li je vreme u dozvoljenom opsegu (7:00 - 22:00)
      if (startTime.getHours() >= minHour && endTime.getHours() <= maxEndHour && 
          (endTime.getHours() < maxEndHour || endTime.getMinutes() === 0)) {
        suggestions.push({
          startHour: String(startTime.getHours()).padStart(2, '0'),
          startMinute: String(startTime.getMinutes()).padStart(2, '0'),
          endHour: String(endTime.getHours()).padStart(2, '0'),
          endMinute: String(endTime.getMinutes()).padStart(2, '0')
        });
      }
      
      // Ako smo dostigli 9 tagova, prekini
      if (suggestions.length >= 9) break;
    }

    return suggestions;
  };

  const applySuggestedTime = (suggestion) => {
    setStartTime({ hour: suggestion.startHour, minute: suggestion.startMinute });
    setEndTime({ hour: suggestion.endHour, minute: suggestion.endMinute });
  };

  const handleSubmit = async () => {
    if (!canManageMenus) return;
    if (!selectedMeal || !selectedDate || isSubmitting) return;

    if (selectedMealType === null) {
      setNotification({
        kind: 'warning',
        title: 'Obavezno polje',
        subtitle: 'Izaberite tip obroka pre potvrde.'
      });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setIsSubmitting(true);

    try {
      // Kreiraj ISO datetime stringove
      const startDateTime = new Date(selectedDate);
      startDateTime.setHours(parseInt(startTime.hour), parseInt(startTime.minute), 0, 0);
      
      const endDateTime = new Date(selectedDate);
      endDateTime.setHours(parseInt(endTime.hour), parseInt(endTime.minute), 0, 0);

      const body = {
        mealId: selectedMeal.id,
        mealType: selectedMealType,
        dietPlanId: dietPlanId,
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        repeatEveryWeek: repeatEveryWeek
      };

      const response = await axiosInstance.post('/api/DailyMenu', body);
      
      if (response.data.success) {
        // Uspešno dodato, prikaži notifikaciju
        setNotification({
          kind: 'success',
          title: 'Uspešno dodato',
          subtitle: `${selectedMeal.name} je dodat u jelovnik`
        });
        
        // Sakrij notifikaciju posle 3 sekunde
        setTimeout(() => setNotification(null), 3000);
        
        // Zatvori panel i osveži podatke
        closeSidePanel();
        fetchMenuData();
      }
    } catch (err) {
      console.error('Greška pri dodavanju menija:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: 'Nije moguće dodati obrok u jelovnik'
      });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMenu = (menu, e) => {
    e.stopPropagation();
    // Brisanje obroka iz jelovnika zahteva dozvolu `menus.manage`.
    if (!canManageMenus) return;
    setMenuToDelete(menu);
    setIsDeleteModalOpen(true);
  };

  const openViewPanel = (menu) => {
    setViewingMenu(menu);
    setIsViewPanelOpen(true);
  };

  const closeViewPanel = () => {
    setIsViewPanelOpen(false);
    setViewingMenu(null);
    setIsMealImageModalOpen(false);
  };

  const confirmDelete = async () => {
    if (!canManageMenus || !menuToDelete) return;
    
    try {
      await axiosInstance.delete(`/api/DailyMenu/${menuToDelete.id}`);
      
      setNotification({
        kind: 'success',
        title: 'Uspešno!',
        subtitle: `${menuToDelete.mealName} je uklonjen iz jelovnika.`
      });
      
      setTimeout(() => setNotification(null), 3000);
      
      // Osveži podatke
      fetchMenuData();
    } catch (err) {
      console.error('Greška pri brisanju menija:', err);
      setNotification({
        kind: 'error',
        title: 'Greška!',
        subtitle: 'Nije moguće obrisati obrok iz jelovnika.'
      });
      
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsDeleteModalOpen(false);
      setMenuToDelete(null);
    }
  };

  const confirmDeleteWeek = async () => {
    if (!canManageMenus) return;
    try {
      await axiosInstance.delete('/api/DailyMenu/week', {
        params: { weekOffset }
      });
      
      setNotification({
        kind: 'success',
        title: 'Uspešno!',
        subtitle: 'Svi obroci za ovu nedelju su uklonjeni iz jelovnika.'
      });
      
      setTimeout(() => setNotification(null), 3000);
      
      // Osveži podatke
      fetchMenuData();
    } catch (err) {
      console.error('Greška pri brisanju nedelje:', err);
      setNotification({
        kind: 'error',
        title: 'Greška!',
        subtitle: 'Nije moguće obrisati obroke za ovu nedelju.'
      });
      
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsDeleteWeekModalOpen(false);
    }
  };

  const renderDayView = () => {
    if (!dayViewDate || loading) {
      return (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SkeletonPlaceholder style={{ width: '100%', height: '300px' }} />
        </div>
      );
    }

    const viewStyle = {
      flex: 1,
      opacity: isViewTransitioning ? 0 : 1,
      transform: isViewTransitioning ? 'translateY(20px)' : 'translateY(0)',
      transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    };

    const groupedMenus = groupMenusByDay();
    const dayKey = dayViewDate.toDateString();
    const dayMenus = groupedMenus[dayKey] || [];

    if (dayMenus.length === 0) {
      return (
        <div style={{ 
          ...viewStyle,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{
            padding: '3rem 2rem',
            borderRadius: '12px',
            backgroundColor: 'var(--cds-layer-01, rgba(255, 255, 255, 0.05))',
            border: '1px solid var(--cds-border-subtle-00, rgba(255, 255, 255, 0.08))',
            textAlign: 'center',
            maxWidth: '400px'
          }}>
            <div style={{ 
              fontSize: '1.15rem', 
              color: 'var(--cds-text-secondary, #8d8d8d)',
              fontWeight: '500',
              fontStyle: 'italic',
              marginBottom: '1.5rem'
            }}>
              Nema zakazanih obroka
            </div>
            {canManageMenus && (
            <div
              style={{ 
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: 'rgba(159, 183, 70, 0.15)',
                border: '2px solid #9fb746',
                color: '#9fb746',
                transition: 'all 0.2s ease',
                margin: '0 auto'
              }}
              onClick={() => {
                setSelectedDate(dayViewDate);
                setIsSidePanelOpen(true);
                fetchMeals();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#9fb746';
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(159, 183, 70, 0.15)';
                e.currentTarget.style.color = '#9fb746';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Add size={32} />
            </div>
            )}
          </div>
        </div>
      );
    }

    // Sort meals by time
    const sortedMenus = [...dayMenus].sort((a, b) => 
      new Date(a.startTime) - new Date(b.startTime)
    );

    return (
      <div style={{ 
        ...viewStyle,
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gridTemplateRows: isMobile ? 'auto' : 'repeat(2, 1fr)',
        gap: '1rem',
        width: '100%',
        height: '100%'
      }}>
        {sortedMenus.map((menu, idx) => (
          <div
            key={idx}
            style={{
              backgroundImage: menu.mealImgUrl 
                ? `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%), url(${menu.mealImgUrl})`
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              height: '100%',
              width: '100%',
              position: 'relative',
              borderRadius: '0.75rem',
              overflow: 'hidden',
              border: '1px solid var(--cds-border-subtle, #e0e0e0)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '1rem'
            }}
            onClick={() => openViewPanel(menu)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            }}
          >
            {/* Actions at top right */}
            {canManageMenus && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              alignItems: 'flex-start'
            }}>
              <Button
                kind="danger--ghost"
                size="sm"
                renderIcon={TrashCan}
                hasIconOnly
                onClick={(e) => handleDeleteMenu(menu, e)}
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }}
              />
            </div>
            )}

            {/* Content at bottom */}
            <div>
              <h3 style={{
                margin: 0,
                marginBottom: '0.5rem',
                color: '#ffffff',
                fontSize: '1.25rem',
                fontWeight: '600',
                textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                lineHeight: 1.2
              }}>
                {menu.mealName}
              </h3>
              <div style={{ 
                fontSize: '0.875rem',
                color: 'rgba(255, 255, 255, 0.85)',
                marginBottom: '0.5rem',
                textShadow: '0 1px 3px rgba(0,0,0,0.8)'
              }}>
                {menu.menuName}
              </div>
              <div style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#9fb746',
                textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                backgroundColor: 'rgba(0,0,0,0.4)',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                {formatTime(menu.startTime)} - {formatTime(menu.endTime)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMobileMealCard = (menu) => (
    <div
      key={menu.id || menu.mealName}
      style={{
        position: 'relative',
        borderRadius: '8px',
        cursor: 'pointer',
        backgroundImage: menu.mealImgUrl
          ? `linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%), url(${menu.mealImgUrl})`
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        border: '1px solid var(--cds-border-subtle-01)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: '0.75rem',
        overflow: 'hidden',
        minHeight: '90px'
      }}
      onClick={() => openViewPanel(menu)}
    >
      {canManageMenus && (
      <div style={{ display: 'flex', justifyContent: 'flex-end', position: 'absolute', top: '0.5rem', right: '0.5rem' }}>
        <Button
          kind="danger--ghost"
          size="sm"
          renderIcon={TrashCan}
          hasIconOnly
          onClick={(e) => handleDeleteMenu(menu, e)}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        />
      </div>
      )}
      <div style={{ fontWeight: '600', fontSize: '0.9375rem', color: '#ffffff', textShadow: '0 2px 4px rgba(0,0,0,0.8)', marginBottom: '0.25rem', lineHeight: 1.2 }}>
        {menu.mealName}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#9fb746', fontWeight: '600', textShadow: '0 1px 3px rgba(0,0,0,0.8)', backgroundColor: 'rgba(0,0,0,0.4)', padding: '0.25rem 0.5rem', borderRadius: '4px', display: 'inline-block', alignSelf: 'flex-start' }}>
        {formatTime(menu.startTime)} - {formatTime(menu.endTime)}
      </div>
    </div>
  );

  const renderWeekView = () => {
    if (loading) {
      if (isMobile) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Date range skeleton */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.25rem' }}>
              <SkeletonPlaceholder style={{ height: '28px', width: '180px', borderRadius: '12px' }} />
            </div>
            {[...Array(7)].map((_, idx) => (
              <div key={idx} style={{ padding: '0.75rem', borderRadius: '8px', backgroundColor: 'var(--cds-layer-01)', border: '1px solid var(--cds-border-subtle-00)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', margin: '-0.75rem -0.75rem 0 -0.75rem', backgroundColor: 'rgba(159, 183, 70, 0.15)', borderRadius: '8px 8px 0 0', borderLeft: '4px solid #9fb746' }}>
                  <SkeletonPlaceholder style={{ height: '16px', width: '80px' }} />
                  <SkeletonPlaceholder style={{ height: '16px', width: '60px' }} />
                </div>
                <SkeletonPlaceholder style={{ height: '90px', borderRadius: '8px' }} />
                <SkeletonPlaceholder style={{ height: '90px', borderRadius: '8px' }} />
              </div>
            ))}
          </div>
        );
      }
      return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '0.5rem',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid var(--cds-border-subtle-01, rgba(255, 255, 255, 0.1))'
          }}>
            {['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'].map(day => (
              <div key={day} style={{ 
                textAlign: 'center', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                color: 'var(--cds-text-secondary, #9fb746)',
                letterSpacing: '0.5px',
                padding: '0.25rem 0'
              }}>
                {day}
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', flex: 1 }}>
            {[...Array(7)].map((_, idx) => (
              <div key={idx} style={{ 
                padding: '0.5rem 1rem', 
                backgroundColor: 'var(--cds-layer-01)', 
                borderRadius: '8px',
                border: '1px solid var(--cds-border-subtle-00)',
                minHeight: '300px'
              }}>
                <SkeletonPlaceholder style={{ height: '40px', width: '60px', marginBottom: '1rem' }} />
                <SkeletonPlaceholder style={{ height: '100px', marginBottom: '0.5rem' }} />
                <SkeletonPlaceholder style={{ height: '20px', width: '80%' }} />
              </div>
            ))}
          </div>
        </div>
      );
    }

    const groupedMenus = groupMenusByDay();
    const startDate = new Date(menuData?.weekStartDate || new Date());
    const weekDays = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dayKey = date.toDateString();
      weekDays.push({
        date,
        dateKey: dayKey,
        isToday: date.toDateString() === new Date().toDateString(),
        menus: groupedMenus[dayKey] || []
      });
    }

    const dayNames = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];
    const weekDayHeaders = weekDays.map(day => {
      const dayOfWeek = day.date.getDay();
      return dayNames[dayOfWeek];
    });

    if (isMobile) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {menuData && menuData.weekStartDate && menuData.weekEndDate && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.25rem' }}>
              <span style={{ 
                padding: '0.35rem 1rem',
                backgroundColor: 'var(--cds-layer-accent-01)',
                borderRadius: '12px',
                fontSize: '0.8125rem',
                fontWeight: '500'
              }}>
                {new Date(menuData.weekStartDate).toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'short' })} - {new Date(menuData.weekEndDate).toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          )}
          {weekDays.map((day, idx) => {
            const hasMenus = day.menus.length > 0;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const cellDate = new Date(day.date);
            cellDate.setHours(0, 0, 0, 0);
            const isPast = cellDate < today;
            return (
              <div
                key={idx}
                style={{
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: day.isToday ? 'rgba(159,183,70,0.15)' : hasMenus ? 'rgba(159,183,70,0.06)' : 'var(--cds-layer-01, rgba(255,255,255,0.04))',
                  border: day.isToday ? '2px solid #9fb746' : hasMenus ? '1px solid rgba(159,183,70,0.25)' : '1px solid var(--cds-border-subtle-00)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', margin: '-0.75rem -0.75rem 0 -0.75rem', backgroundColor: 'rgba(159, 183, 70, 0.15)', borderRadius: '8px 8px 0 0', borderLeft: '4px solid #9fb746' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--cds-text-primary)', textTransform: 'uppercase' }}>
                    {dayNames[day.date.getDay()]}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--cds-text-secondary)' }}>
                      {day.date.getDate()}.{day.date.getMonth() + 1}.
                    </span>
                    {!isPast && canManageMenus && (
                      <div
                        className="calendar-add-menu-btn"
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(159, 183, 70, 0.15)',
                          border: '2px solid #9fb746',
                          color: '#9fb746',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDateSelection(day.date);
                        }}
                      >
                        <Add size={18} />
                      </div>
                    )}
                  </div>
                </div>
                {day.menus.length === 0 ? (
                  <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', textAlign: 'center', fontStyle: 'italic', opacity: 0.6 }}>
                    Nema obroka
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {day.menus.sort((a, b) => new Date(a.startTime) - new Date(b.startTime)).map((menu) => renderMobileMealCard(menu))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <style>
          {`
            .week-grid {
              transition: opacity 0.2s ease, transform 0.2s ease;
            }

            .week-grid.transitioning {
              opacity: 0;
              transform: scale(0.98);
            }

            @media (min-width: 2400px) {
              .week-day-cell {
                min-height: 700px !important;
              }
              .week-day-cell .meal-container {
                max-height: 640px !important;
                grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important;
              }
              .menu-item-simple {
                height: 100px !important;
              }
            }
            @media (min-width: 2000px) and (max-width: 2399px) {
              .week-day-cell {
                min-height: 600px !important;
              }
              .week-day-cell .meal-container {
                max-height: 540px !important;
                grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important;
              }
              .menu-item-simple {
                height: 100px !important;
              }
            }
            @media (min-width: 1600px) and (max-width: 1999px) {
              .week-day-cell {
                min-height: 450px !important;
              }
              .week-day-cell .meal-container {
                max-height: 390px !important;
                grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)) !important;
              }
              .menu-item-simple {
                height: 85px !important;
                font-size: 0.75rem !important;
              }
            }
            @media (min-width: 1280px) and (max-width: 1599px) {
              .week-day-cell {
                min-height: 380px !important;
              }
              .week-day-cell .meal-container {
                max-height: 320px !important;
                grid-template-columns: repeat(auto-fill, minmax(95px, 1fr)) !important;
              }
              .menu-item-simple {
                height: 75px !important;
                font-size: 0.7rem !important;
              }
            }
            @media (max-width: 768px) {
              .week-header {
                grid-template-columns: repeat(7, 1fr) !important;
              }
              .week-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                grid-auto-rows: minmax(120px, auto) !important;
              }
              .week-day-cell {
                min-height: 120px !important;
              }
            }
            @media (max-width: 480px) {
              .week-grid {
                grid-template-columns: 1fr !important;
              }
            }
          `}
        </style>
        <div 
          className="week-header"
          style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '0.5rem',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid var(--cds-border-subtle-01, rgba(255, 255, 255, 0.1))'
          }}
        >
          {weekDayHeaders.map((day, idx) => (
            <div key={idx} style={{ 
              textAlign: 'center', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: 'var(--cds-text-secondary, #9fb746)',
              letterSpacing: '0.5px',
              padding: '0.25rem 0'
            }}>
              {day}
            </div>
          ))}
        </div>
        <div 
          className={`week-grid ${isViewTransitioning ? 'transitioning' : ''}`}
          style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '0.5rem',
            flex: 1
          }}
        >
          {weekDays.map((day, idx) => {
            const hasMenus = day.menus.length > 0;
            const isDateSelected = selectedDates.some(d => formatDateKey(d) === formatDateKey(day.date));
            
            // Check if date is in the past
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const cellDate = new Date(day.date);
            cellDate.setHours(0, 0, 0, 0);
            const isPast = cellDate < today;
            
            return (
              <div
                key={idx}
                className="week-day-cell"
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  backgroundColor: day.isToday 
                    ? 'var(--cds-layer-selected-01, rgba(159, 183, 70, 0.15))' 
                    : hasMenus 
                      ? 'var(--cds-layer-accent-01, rgba(159, 183, 70, 0.08))' 
                      : 'var(--cds-layer-01, rgba(255, 255, 255, 0.05))',
                  border: day.isToday 
                    ? '2px solid var(--cds-focus, #9fb746)' 
                    : hasMenus 
                      ? '1px solid var(--cds-border-subtle-01, rgba(159, 183, 70, 0.25))' 
                      : '1px solid var(--cds-border-subtle-00, rgba(255, 255, 255, 0.08))',
                  transition: 'all 0.15s cubic-bezier(0.2, 0, 0.38, 0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.375rem',
                  minHeight: '300px',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setIsViewTransitioning(true);
                  setTimeout(() => {
                    setDayViewDate(day.date);
                    setViewMode('day');
                    setTimeout(() => setIsViewTransitioning(false), 50);
                  }, 200);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cds-layer-hover-01, rgba(159, 183, 70, 0.12))';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = day.isToday 
                    ? 'var(--cds-layer-selected-01, rgba(159, 183, 70, 0.15))' 
                    : hasMenus 
                      ? 'var(--cds-layer-accent-01, rgba(159, 183, 70, 0.08))' 
                      : 'var(--cds-layer-01, rgba(255, 255, 255, 0.05))';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: day.isToday ? '500' : hasMenus ? '600' : '400',
                  lineHeight: 1,
                  color: day.isToday ? '#0f62fe' : hasMenus ? '#9fb746' : 'inherit'
                }}>
                  {day.date.getDate()}
                </div>
                
                {!isPast && canManageMenus && (
                  <div 
                    className="calendar-add-menu-btn" 
                    style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backgroundColor: isDateSelected ? '#9fb746' : 'rgba(159, 183, 70, 0.15)',
                      border: '2px solid #9fb746',
                      color: isDateSelected ? '#ffffff' : '#9fb746',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDateSelection(day.date);
                    }}
                    onMouseEnter={(e) => {
                      if (!isDateSelected) {
                        e.currentTarget.style.backgroundColor = 'rgba(159, 183, 70, 0.25)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDateSelected) {
                        e.currentTarget.style.backgroundColor = 'rgba(159, 183, 70, 0.15)';
                      }
                    }}
                  >
                    {isDateSelected ? <Checkmark size={24} /> : <Add size={24} />}
                  </div>
                )}
                
                {day.menus.length === 0 ? (
                  <div style={{ 
                    fontSize: '0.75rem',
                    color: 'var(--cds-text-secondary)',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    opacity: 0.6
                  }}>
                    Nema obroka
                  </div>
                ) : (
                  <div 
                    className="meal-container"
                    style={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: '0.5rem',
                    width: '100%',
                    overflowY: 'auto',
                    maxHeight: '280px',
                    paddingRight: '0.25rem'
                  }}>
                    {day.menus.map((menu, menuIdx) => (
                      <div
                        key={menuIdx}
                        className="menu-item-simple"
                        style={{
                          position: 'relative',
                          height: '100px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundImage: menu.mealImgUrl 
                            ? `linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%), url(${menu.mealImgUrl})`
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          border: '1px solid var(--cds-border-subtle-01)',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                          padding: '0.625rem',
                          overflow: 'hidden'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openViewPanel(menu);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ 
                          fontWeight: '600',
                          fontSize: '0.8125rem',
                          color: '#ffffff',
                          textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                          marginBottom: '0.25rem',
                          lineHeight: '1.2',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {menu.mealName}
                        </div>
                        <div style={{ 
                          fontSize: '0.6875rem',
                          color: '#9fb746',
                          fontWeight: '600',
                          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                          backgroundColor: 'rgba(0,0,0,0.4)',
                          padding: '0.125rem 0.375rem',
                          borderRadius: '3px',
                          display: 'inline-block',
                          alignSelf: 'flex-start'
                        }}>
                          {formatTime(menu.startTime)} - {formatTime(menu.endTime)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getWeekLabel = () => {
    if (!menuData) return '';
    const startDate = new Date(menuData.weekStartDate);
    const endDate = new Date(menuData.weekEndDate);
    return `${startDate.getDate()}.${startDate.getMonth() + 1}.${startDate.getFullYear()} - ${endDate.getDate()}.${endDate.getMonth() + 1}.${endDate.getFullYear()}`;
  };

  const filteredMeals = meals.filter(meal => 
    !searchTerm || meal.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pageSize = isMobile ? 4 : 6;
  const totalPages = Math.ceil(filteredMeals.length / pageSize);
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const paginatedMeals = filteredMeals.slice(start, end);

  const getSubtitle = () => {
    if (viewMode === 'day' && dayViewDate) {
      const days = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
      const months = ['januar', 'februar', 'mart', 'april', 'maj', 'jun', 'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];
      return `${days[dayViewDate.getDay()]}, ${dayViewDate.getDate()}. ${months[dayViewDate.getMonth()]} ${dayViewDate.getFullYear()}`;
    }
    return menuData ? `Nedelja ${menuData.weekNumber}/${menuData.year} - ${getWeekLabel()}` : 'Učitavanje...';
  };

  return (
    <PageContainer 
      title="Nedeljni jelovnik" 
      subtitle={getSubtitle()}
    >
      {/* Notification */}
      {notification && (
        <div style={{ 
          position: 'fixed', 
          top: '4rem', 
          right: '1rem', 
          zIndex: 9999,
          minWidth: '320px',
          animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <style>
            {`
              @keyframes slideInFromRight {
                from {
                  transform: translateX(calc(100% + 1rem));
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
          </style>
          <InlineNotification
            kind={notification.kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
      )}

      <Tile className="menu-tile" style={{ padding: '1.5rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <div className="menu-desktop-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'nowrap' }}>
            <Button
              kind="ghost"
              size="md"
              renderIcon={ChevronLeft}
              iconDescription={viewMode === 'day' ? 'Prethodni dan' : 'Prethodna nedelja'}
              hasIconOnly
              onClick={() => viewMode === 'day' ? navigateDay(-1) : navigateWeek(-1)}
            />
            <Button
              kind="secondary"
              size="md"
              style={{ width: '180px', justifyContent: 'center', whiteSpace: 'nowrap', height: '40px' }}
              onClick={viewMode === 'day' ? goToToday : goToCurrentWeek}
              disabled={viewMode === 'day' ? (dayViewDate && dayViewDate.toDateString() === new Date().toDateString()) : (weekOffset === 0)}
            >
              {viewMode === 'day' ? 'Današnji dan' : 'Tekuća nedelja'}
            </Button>
            <Button
              kind="ghost"
              size="md"
              renderIcon={ChevronRight}
              iconDescription={viewMode === 'day' ? 'Sledeći dan' : 'Sledeća nedelja'}
              hasIconOnly
              onClick={() => viewMode === 'day' ? navigateDay(1) : navigateWeek(1)}
            />
            <div style={{ minWidth: '250px' }}>
              <Dropdown
                id="diet-plan-filter"
                titleText=""
                label={dietTypes.find(dt => dt.id === dietPlanId)?.name || 'Tip dijete...'}
                items={dietTypes.map(dt => ({ id: dt.id, text: dt.name }))}
                itemToString={(item) => item ? item.text : ''}
                selectedItem={dietTypes.find(dt => dt.id === dietPlanId) ? { id: dietPlanId, text: dietTypes.find(dt => dt.id === dietPlanId).name } : null}
                onChange={({ selectedItem }) => {
                  setDietPlanId(selectedItem?.id || '');
                }}
                size="md"
              />
            </div>
            <div style={{ minWidth: '180px' }}>
              <Dropdown
                id="view-mode-filter"
                titleText=""
                label={viewMode === 'week' ? 'Nedeljni prikaz' : 'Dnevni prikaz'}
                items={[
                  { id: 'week', text: 'Nedeljni prikaz' },
                  { id: 'day', text: 'Dnevni prikaz' }
                ]}
                itemToString={(item) => item ? item.text : ''}
                selectedItem={viewMode === 'week' ? { id: 'week', text: 'Nedeljni prikaz' } : { id: 'day', text: 'Dnevni prikaz' }}
                onChange={({ selectedItem }) => {
                  if (selectedItem?.id === 'day' && !dayViewDate) {
                    setDayViewDate(new Date());
                  }
                  setViewMode(selectedItem?.id || 'week');
                }}
                size="md"
              />
            </div>
            {canManageMenus && viewMode === 'week' && menuData && menuData.totalMenus > 0 && (
              <Button
                kind="danger"
                size="md"
                renderIcon={TrashCan}
                onClick={() => setIsDeleteWeekModalOpen(true)}
              >
                Obriši nedelju
              </Button>
            )}
          </div>

          {viewMode === 'week' && menuData && menuData.weekStartDate && menuData.weekEndDate && (
            <div style={{ 
              fontSize: '0.875rem',
              color: 'var(--cds-text-secondary)',
              display: 'flex',
              gap: '1rem',
              alignItems: 'center'
            }}>
              <span style={{ 
                padding: '0.25rem 0.75rem',
                backgroundColor: 'var(--cds-layer-accent-01)',
                borderRadius: '12px',
                fontSize: '0.8125rem'
              }}>
                {new Date(menuData.weekStartDate).toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'short' })} - {new Date(menuData.weekEndDate).toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          )}
          {viewMode === 'day' && dayViewDate && (
            <div style={{ 
              fontSize: '0.875rem',
              color: 'var(--cds-text-secondary)',
              display: 'flex',
              gap: '1rem',
              alignItems: 'center'
            }}>
              <span style={{ 
                padding: '0.25rem 0.75rem',
                backgroundColor: 'var(--cds-layer-accent-01)',
                borderRadius: '12px',
                fontSize: '0.8125rem',
                fontWeight: '500'
              }}>
                {dayViewDate.toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
          )}
        </div>

        {viewMode === 'week' ? renderWeekView() : renderDayView()}
      </Tile>

      {/* Floating Error Notification */}
      {error && (
        <div style={{ 
          position: 'fixed', 
          top: '4rem', 
          right: '1rem', 
          zIndex: 9999,
          minWidth: '320px',
          animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <style>
            {`
              @keyframes slideInFromRight {
                from {
                  transform: translateX(calc(100% + 1rem));
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
          </style>
          <InlineNotification
            kind="error"
            title="Greška"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
      )}

      {/* Right Side Panel - add only, requires `menus.manage` */}
      {canManageMenus && (
      <div className={`menu-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header">
          <h2>Dodaj obrok za jelovnik</h2>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            hasIconOnly
            onClick={closeSidePanel}
          />
        </div>

        {/* Progress Indicator */}
        <div style={{ 
          padding: '1rem 1.5rem 0.75rem', 
          borderBottom: '1px solid var(--cds-border-subtle)',
          backgroundColor: 'var(--cds-layer-01)'
        }}>
          <div className="custom-progress-bar">
            <div className="progress-steps">
              <div 
                className={`progress-step ${currentStep >= 0 ? 'completed' : ''} ${currentStep === 0 ? 'active' : ''}`}
                onClick={() => handleStepClick(0)}
                style={{ cursor: 'pointer' }}
              >
                <div className="step-circle">
                  {currentStep > 0 ? <Checkmark size={16} /> : '1'}
                </div>
                <div className="step-label">
                  <div className="step-title">Izaberi obrok</div>
                  {currentStep > 0 && selectedMeal && (
                    <div className="step-description">{selectedMeal.name}</div>
                  )}
                </div>
              </div>
              <div className={`progress-line ${currentStep >= 1 ? 'completed' : ''}`}></div>
              <div 
                className={`progress-step ${currentStep >= 1 ? 'completed' : ''} ${currentStep === 1 ? 'active' : ''}`}
                onClick={() => handleStepClick(1)}
                style={{ cursor: 'pointer' }}
              >
                <div className="step-circle">
                  {currentStep > 1 ? <Checkmark size={16} /> : '2'}
                </div>
                <div className="step-label">
                  <div className="step-title">Podesi vreme</div>
                  {currentStep > 1 && (
                    <div className="step-description">{startTime.hour}:{startTime.minute} - {endTime.hour}:{endTime.minute}</div>
                  )}
                </div>
              </div>
              <div className={`progress-line ${currentStep >= 2 ? 'completed' : ''}`}></div>
              <div 
                className={`progress-step ${currentStep >= 2 ? 'completed' : ''} ${currentStep === 2 ? 'active' : ''}`}
                onClick={() => handleStepClick(2)}
                style={{ cursor: 'pointer' }}
              >
                <div className="step-circle">
                  {currentStep > 2 ? <Checkmark size={16} /> : '3'}
                </div>
                <div className="step-label">
                  <div className="step-title">Potvrdi</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="side-panel-content">
          {currentStep === 0 && (
            <div className={`step-content ${stepTransition}`}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '1.5rem'
              }}>
                <Search
                  size="md"
                  placeholder="Pretraži obroke..."
                  labelText="Pretraga"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  onClear={() => {
                    setSearchTerm('');
                    setPage(1);
                  }}
                  style={{ flex: 1 }}
                />
                {filteredMeals.length > pageSize && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem'
                  }}>
                    <Button
                      kind="ghost"
                      size="md"
                      renderIcon={ChevronLeft}
                      onClick={prevPage}
                      hasIconOnly
                      disabled={page === 1}
                    />
                    <span style={{ 
                      fontSize: '0.875rem', 
                      opacity: 0.8,
                      minWidth: '60px',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {page}/{totalPages}
                    </span>
                    <Button
                      kind="ghost"
                      size="md"
                      renderIcon={ChevronRight}
                      onClick={nextPage}
                      hasIconOnly
                      disabled={page === totalPages}
                    />
                  </div>
                )}
              </div>
              
              {mealsLoading ? (
                <div className="meals-grid">
                  {[...Array(6)].map((_, idx) => (
                    <div key={idx} style={{ height: '120px' }}>
                      <SkeletonPlaceholder style={{ width: '100%', height: '100%' }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div 
                  className="meals-grid"
                  style={{
                    opacity: pageFading ? 0 : 1,
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  {filteredMeals.length === 0 ? (
                    <div style={{ 
                      gridColumn: '1 / -1',
                      textAlign: 'center', 
                      padding: '3rem 1rem',
                      color: 'var(--cds-text-secondary)'
                    }}>
                      {searchTerm ? 'Nema obroka koji odgovaraju pretrazi' : 'Nema dostupnih obroka'}
                    </div>
                  ) : (
                    paginatedMeals.map((meal) => {
                      const isSelected = selectedMeal?.id === meal.id;
                      return (
                        <div
                          key={meal.id}
                          className={`meal-card-grid ${isSelected ? 'selected' : ''}`}
                          style={{
                            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%), url(${meal.imgUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            height: '120px',
                            borderRadius: '8px',
                            padding: '0.75rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '1px solid var(--cds-border-subtle-01)',
                            position: 'relative'
                          }}
                          onClick={() => handleMealSelect(meal)}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.transform = 'translateY(-4px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        >
                          {isSelected && (
                            <div className="meal-selected-overlay">
                              <div className="meal-selected-checkmark">
                                <Checkmark size={32} />
                              </div>
                            </div>
                          )}
                          <h3 style={{
                            margin: 0,
                            marginBottom: '0.375rem',
                            color: '#ffffff',
                            fontSize: '0.9375rem',
                            fontWeight: '600',
                            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                            lineHeight: 1.2,
                            position: 'relative',
                            zIndex: 1
                          }}>
                            {meal.name}
                          </h3>
                          {meal.description && (
                            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', position: 'relative', zIndex: 1, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                              {meal.description}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}

          {currentStep === 1 && selectedMeal && (
            <div className={`step-content ${stepTransition}`}>
              <h3 style={{ marginBottom: '1rem' }}>Podesi vreme za: {selectedMeal.name}</h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', marginBottom: '1.5rem' }}>
                Izabrani datum: {selectedDate?.toLocaleDateString('sr-Latn-RS', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              {/* Predložena vremena */}
              {(() => {
                const suggestions = getSuggestedTimes();
                return suggestions.length > 0 ? (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: '500' }}>
                      Brzi izbor vremena
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {suggestions.map((suggestion, idx) => (
                        <Tag
                          key={idx}
                          type="green"
                          size="md"
                          onClick={() => applySuggestedTime(suggestion)}
                          style={{ cursor: 'pointer' }}
                        >
                          {suggestion.startHour}:{suggestion.startMinute} - {suggestion.endHour}:{suggestion.endMinute}
                        </Tag>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                  Vreme početka
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <TimePicker
                    id="start-time"
                    labelText=""
                    value={`${startTime.hour}:${startTime.minute}`}
                    onChange={(e) => {
                      const [hour, minute] = e.target.value.split(':');
                      setStartTime({ hour, minute });
                    }}
                  >
                    <TimePickerSelect
                      id="start-hour"
                      labelText="Sati"
                      value={startTime.hour}
                      onChange={(e) => setStartTime({ ...startTime, hour: e.target.value })}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </TimePickerSelect>
                    <TimePickerSelect
                      id="start-minute"
                      labelText="Minuti"
                      value={startTime.minute}
                      onChange={(e) => setStartTime({ ...startTime, minute: e.target.value })}
                    >
                      {Array.from({ length: 60 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </TimePickerSelect>
                  </TimePicker>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                  Vreme kraja
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <TimePicker
                    id="end-time"
                    labelText=""
                    value={`${endTime.hour}:${endTime.minute}`}
                    onChange={(e) => {
                      const [hour, minute] = e.target.value.split(':');
                      setEndTime({ hour, minute });
                    }}
                  >
                    <TimePickerSelect
                      id="end-hour"
                      labelText="Sati"
                      value={endTime.hour}
                      onChange={(e) => setEndTime({ ...endTime, hour: e.target.value })}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </TimePickerSelect>
                    <TimePickerSelect
                      id="end-minute"
                      labelText="Minuti"
                      value={endTime.minute}
                      onChange={(e) => setEndTime({ ...endTime, minute: e.target.value })}
                    >
                      {Array.from({ length: 60 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </TimePickerSelect>
                  </TimePicker>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: '500' }}>
                  Tip obroka
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {mealTypes.map((type) => {
                    const value = MEAL_TYPE_VALUE_MAP[type.value];
                    if (value === undefined) return null;
                    return (
                      <Tag
                        key={type.value}
                        type={selectedMealType === value ? 'green' : 'gray'}
                        size="md"
                        onClick={() => setSelectedMealType(value)}
                        style={{ cursor: 'pointer' }}
                      >
                        {type.displayName}
                      </Tag>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <Button kind="secondary" onClick={() => goToStep(0)} style={{ flex: 1 }}>
                  Nazad
                </Button>
                <Button kind="primary" onClick={() => goToStep(2)} style={{ flex: 1 }}>
                  Dalje
                </Button>
              </div>
            </div>
          )}

          {currentStep === 2 && selectedMeal && (
            <div className={`step-content ${stepTransition}`}>
              <h3 style={{ marginBottom: '1rem' }}>Potvrdi dodavanje</h3>
              <div style={{ 
                padding: '1rem', 
                backgroundColor: 'var(--cds-layer-02)', 
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Obrok:</strong> {selectedMeal.name}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Datum:</strong> {selectedDate?.toLocaleDateString('sr-Latn-RS', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Vreme:</strong> {startTime.hour}:{startTime.minute} - {endTime.hour}:{endTime.minute}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Tip obroka:</strong> {getMealTypeDisplayName(selectedMealType)}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Tip jelovnika:</strong> {menuName}
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Ponavljanje:</strong> {
                    repeatEveryWeek === 0 ? 'Samo jednom' :
                    repeatEveryWeek === 1 ? 'Svake nedelje' :
                    repeatEveryWeek === 2 ? 'Svake druge nedelje' :
                    'Svake treće nedelje'
                  }
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: '500' }}>
                  Ponavljanje
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Tag
                    type={repeatEveryWeek === 0 ? 'green' : 'gray'}
                    size="md"
                    onClick={() => setRepeatEveryWeek(0)}
                    style={{ cursor: 'pointer' }}
                  >
                    Samo jednom
                  </Tag>
                  <Tag
                    type={repeatEveryWeek === 1 ? 'green' : 'gray'}
                    size="md"
                    onClick={() => setRepeatEveryWeek(1)}
                    style={{ cursor: 'pointer' }}
                  >
                    Svake nedelje
                  </Tag>
                  <Tag
                    type={repeatEveryWeek === 2 ? 'green' : 'gray'}
                    size="md"
                    onClick={() => setRepeatEveryWeek(2)}
                    style={{ cursor: 'pointer' }}
                  >
                    Svake druge nedelje
                  </Tag>
                  <Tag
                    type={repeatEveryWeek === 3 ? 'green' : 'gray'}
                    size="md"
                    onClick={() => setRepeatEveryWeek(3)}
                    style={{ cursor: 'pointer' }}
                  >
                    Svake treće nedelje
                  </Tag>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button kind="secondary" onClick={() => goToStep(1)} disabled={isSubmitting} style={{ flex: 1 }}>
                  Nazad
                </Button>
                <Button
                  kind="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  renderIcon={Checkmark}
                  style={{ flex: 1 }}
                >
                  {isSubmitting ? 'Dodavanje...' : 'Potvrdi'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Overlay */}
      {isSidePanelOpen && (
        <div 
          className="side-panel-overlay" 
          onClick={closeSidePanel}
        />
      )}

      {/* View Details Side Panel */}
      <div className={`menu-side-panel ${isViewPanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header">
          <h2>Detalji obroka</h2>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            hasIconOnly
            onClick={closeViewPanel}
          />
        </div>

        {viewingMenu && (
          <div className="side-panel-content" style={{ padding: '1.5rem' }}>
            {/* Slika obroka */}
            {viewingMenu.mealImgUrl && (
              <div style={{ marginBottom: '1.5rem' }}>
                <img
                  src={viewingMenu.mealImgUrl}
                  alt={viewingMenu.mealName || 'Obrok'}
                  style={{
                    width: '100%',
                    maxHeight: '260px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    border: '1px solid var(--cds-border-subtle, #e0e0e0)',
                    backgroundColor: 'var(--cds-layer-02, #f4f4f4)',
                    cursor: 'zoom-in'
                  }}
                  onClick={() => setIsMealImageModalOpen(true)}
                />
              </div>
            )}

            {/* Naziv obroka */}
            <h3 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              marginBottom: '1rem',
              color: 'var(--cds-text-primary)'
            }}>
              {viewingMenu.mealName}
            </h3>

            {/* Osnovne informacije */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.75rem', 
                  fontWeight: '600',
                  color: 'var(--cds-text-secondary)',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Vreme
                </label>
                <div style={{ 
                  fontSize: '1rem', 
                  color: '#9fb746',
                  fontWeight: '600'
                }}>
                  {formatTime(viewingMenu.startTime)} - {formatTime(viewingMenu.endTime)}
                </div>
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.75rem', 
                  fontWeight: '600',
                  color: 'var(--cds-text-secondary)',
                  marginBottom: '0.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Datum
                </label>
                <div style={{ fontSize: '1rem' }}>
                  {new Date(viewingMenu.startTime).toLocaleDateString('sr-Latn-RS', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
              </div>

              {viewingMenu.menuName && (
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.75rem', 
                    fontWeight: '600',
                    color: 'var(--cds-text-secondary)',
                    marginBottom: '0.25rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Naziv jelovnika
                  </label>
                  <div style={{ fontSize: '1rem' }}>
                    {viewingMenu.menuName}
                  </div>
                </div>
              )}
            </div>

            {/* Placeholder za dodatne informacije */}
            <div style={{ 
              padding: '1rem',
              backgroundColor: 'var(--cds-layer-01)',
              borderRadius: '8px',
              border: '1px dashed var(--cds-border-subtle-01)',
              marginBottom: '1.5rem'
            }}>
              <p style={{ 
                fontSize: '0.875rem', 
                color: 'var(--cds-text-secondary)',
                fontStyle: 'italic',
                margin: 0
              }}>
                Dodatne informacije će biti prikazane ovde...
              </p>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {canManageMenus && (
                <Button 
                  kind="danger" 
                  size="md"
                  style={{ flex: 1 }}
                  onClick={(e) => {
                    closeViewPanel();
                    handleDeleteMenu(viewingMenu, e);
                  }}
                >
                  Obriši obrok
                </Button>
              )}
              <Button 
                kind="secondary" 
                size="md"
                style={{ flex: 1 }}
                onClick={closeViewPanel}
              >
                Zatvori
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Overlay for View Panel */}
      {isViewPanelOpen && (
        <div 
          className="side-panel-overlay" 
          onClick={closeViewPanel}
        />
      )}

      {/* Meal Image Modal */}
      {isMealImageModalOpen && (
        <Modal
          open
          onRequestClose={() => setIsMealImageModalOpen(false)}
          onSecondarySubmit={() => setIsMealImageModalOpen(false)}
          modalHeading={viewingMenu?.mealName || 'Obrok'}
          size="sm"
          passiveModal
        >
          {viewingMenu?.mealImgUrl && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '0.5rem 0'
              }}
            >
              <img
                src={viewingMenu.mealImgUrl}
                alt={viewingMenu.mealName || 'Obrok'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
            </div>
          )}
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        modalHeading="Brisanje obroka iz jelovnika"
        primaryButtonText="Obriši"
        secondaryButtonText="Otkaži"
        onRequestSubmit={confirmDelete}
        danger
        size="sm"
      >
        <p>
          Da li ste sigurni da želite da uklonite{' '}
          <strong>{menuToDelete?.mealName}</strong> iz jelovnika?
        </p>
        <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
          Ova akcija se ne može poništiti.
        </p>
      </Modal>

      {/* Delete Week Confirmation Modal */}
      <Modal
        open={isDeleteWeekModalOpen}
        onRequestClose={() => setIsDeleteWeekModalOpen(false)}
        modalHeading="Brisanje svih obroka za nedelju"
        primaryButtonText="Obriši sve"
        secondaryButtonText="Otkaži"
        onRequestSubmit={confirmDeleteWeek}
        danger
        size="sm"
      >
        <p>
          Da li ste sigurni da želite da uklonite <strong>sve obroke</strong> za nedelju{' '}
          <strong>{menuData?.weekNumber}/{menuData?.year}</strong>?
        </p>
        <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
          Ukupno {menuData?.totalMenus} obroka će biti obrisano. Ova akcija se ne može poništiti.
        </p>
      </Modal>

      {/* Mobile Filter Button */}
      {!isSidePanelOpen && ReactDOM.createPortal(
        <button
          className="floating-button mobile-filter-floating-btn"
          onClick={() => setIsMobileFilterOpen(true)}
          style={{
            position: 'fixed',
            bottom: '2rem',
            left: '2rem',
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9998
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#8da63d';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#9fb746';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
          }}
        >
          <Filter
            size={24}
            style={{ flexShrink: 0, minWidth: '24px', minHeight: '24px', fill: '#ffffff' }}
            aria-label="Filteri"
            color="#ffffff"
          />
          <span style={{ color: 'white', fontWeight: '500', fontSize: '0.95rem', whiteSpace: 'nowrap', userSelect: 'none' }}>
            Filteri
          </span>
        </button>,
        document.body
      )}

      {/* Mobile Filter Modal */}
      {isMobileFilterOpen && ReactDOM.createPortal(
        <div
          className={isFilterExiting ? 'filter-modal-exiting' : ''}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme === 'g90' || theme === 'g100' ? 'rgba(22, 22, 22, 0.85)' : 'rgba(244, 244, 244, 0.85)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-end',
            animation: isFilterExiting ? 'menuFadeOut 0.2s ease-out' : 'menuFadeIn 0.2s ease-out'
          }}
          onClick={handleCloseMobileFilter}
        >
          <div
            className="mobile-filter-panel"
            data-carbon-theme={theme}
            style={{
              width: '100%',
              backgroundColor: theme === 'g90' || theme === 'g100' ? '#262626' : '#ffffff',
              borderRadius: '16px 16px 0 0',
              padding: '1.5rem',
              animation: isFilterExiting ? 'menuSlideDownOut 0.3s ease-out' : 'menuSlideUpIn 0.3s ease-out',
              maxHeight: '70vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500', color: theme === 'g90' || theme === 'g100' ? '#ffffff' : '#161616' }}>Filteri</h3>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                className="mobile-filter-close-btn"
                renderIcon={Close}
                iconDescription="Zatvori"
                onClick={handleCloseMobileFilter}
                style={{ color: theme === 'g90' || theme === 'g100' ? '#ffffff' : '#161616' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'nowrap', width: '100%' }}>
                <Button
                  kind="primary"
                  size="sm"
                  renderIcon={ChevronLeft}
                  iconDescription={viewMode === 'day' ? 'Prethodni dan' : 'Prethodna nedelja'}
                  hasIconOnly
                  onClick={() => {
                    if (viewMode === 'day') navigateDay(-1); else navigateWeek(-1);
                    handleCloseMobileFilter();
                  }}
                  style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', flex: 1, minWidth: '48px' }}
                />
                <Button
                  kind="primary"
                  size="sm"
                  className="mobile-today-btn"
                  onClick={() => {
                    if (viewMode === 'day') goToToday(); else goToCurrentWeek();
                    handleCloseMobileFilter();
                  }}
                  disabled={viewMode === 'day' ? (dayViewDate && dayViewDate.toDateString() === new Date().toDateString()) : (weekOffset === 0)}
                  style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', whiteSpace: 'nowrap', flex: 1, justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
                >
                  {viewMode === 'day' ? 'Danas' : 'Tekuća nedelja'}
                </Button>
                <Button
                  kind="primary"
                  size="sm"
                  renderIcon={ChevronRight}
                  iconDescription={viewMode === 'day' ? 'Sledeći dan' : 'Sledeća nedelja'}
                  hasIconOnly
                  onClick={() => {
                    if (viewMode === 'day') navigateDay(1); else navigateWeek(1);
                    handleCloseMobileFilter();
                  }}
                  style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', flex: 1, minWidth: '48px' }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                {/* Panel je u portalu (van Carbon <Theme>), pa se boje zadaju eksplicitno
                    da datum ne bi ostao crn na tamnoj pozadini u dark mode-u. */}
                {viewMode === 'week' && menuData && menuData.weekStartDate && menuData.weekEndDate && (
                  <span style={{ 
                    padding: '0.25rem 0.75rem',
                    backgroundColor: theme === 'g90' || theme === 'g100' ? '#393939' : 'var(--cds-layer-accent-01)',
                    color: theme === 'g90' || theme === 'g100' ? '#ffffff' : '#161616',
                    borderRadius: '12px',
                    fontSize: '0.8125rem',
                    fontWeight: '500'
                  }}>
                    {new Date(menuData.weekStartDate).toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'short' })} - {new Date(menuData.weekEndDate).toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
                {viewMode === 'day' && dayViewDate && (
                  <span style={{ 
                    padding: '0.25rem 0.75rem',
                    backgroundColor: theme === 'g90' || theme === 'g100' ? '#393939' : 'var(--cds-layer-accent-01)',
                    color: theme === 'g90' || theme === 'g100' ? '#ffffff' : '#161616',
                    borderRadius: '12px',
                    fontSize: '0.8125rem',
                    fontWeight: '500'
                  }}>
                    {dayViewDate.toLocaleDateString('sr-Latn-RS', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                )}
              </div>
              <Dropdown
                id="mobile-view-filter"
                size="md"
                titleText="Prikaz"
                label={viewMode === 'week' ? 'Nedeljni prikaz' : 'Dnevni prikaz'}
                items={[
                  { id: 'week', text: 'Nedeljni prikaz' },
                  { id: 'day', text: 'Dnevni prikaz' }
                ]}
                itemToString={(item) => item ? item.text : ''}
                selectedItem={viewMode === 'week' ? { id: 'week', text: 'Nedeljni prikaz' } : { id: 'day', text: 'Dnevni prikaz' }}
                onChange={({ selectedItem }) => {
                  if (selectedItem?.id === 'day' && !dayViewDate) {
                    setDayViewDate(new Date());
                  }
                  setViewMode(selectedItem?.id || 'week');
                  handleCloseMobileFilter();
                }}
              />
              <Dropdown
                id="mobile-diet-filter"
                size="md"
                titleText="Tip ishrane"
                label={dietTypes.find(dt => dt.id === dietPlanId)?.name || 'Tip ishrane...'}
                items={dietTypes.map(dt => ({ id: dt.id, text: dt.name }))}
                itemToString={(item) => item ? item.text : ''}
                selectedItem={dietTypes.find(dt => dt.id === dietPlanId) ? { id: dietPlanId, text: dietTypes.find(dt => dt.id === dietPlanId).name } : null}
                onChange={({ selectedItem }) => {
                  setDietPlanId(selectedItem?.id || '');
                  handleCloseMobileFilter();
                }}
              />

              {viewMode === 'week' && menuData && menuData.totalMenus > 0 && (
                <Button
                  kind="danger"
                  size="md"
                  renderIcon={TrashCan}
                  onClick={() => {
                    handleCloseMobileFilter();
                    setIsDeleteWeekModalOpen(true);
                  }}
                >
                  Obriši nedelju
                </Button>
              )}

              <Button
                kind="primary"
                size="md"
                style={{ width: '100%', marginTop: '1rem' }}
                onClick={handleCloseMobileFilter}
              >
                Primeni filtere
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </PageContainer>
  );
};

export default Menu;
